import { CheckboxFormsy } from '@fuse'
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import CancelButton from 'app/components/buttons/CancelButton'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import MessageAlert from 'app/components/MessageAlert'
import effects from 'app/effects/effects'
import { useIsPlaceInRechargeablePeriod } from 'commons/useIsPlaceRestricted'
import clsx from 'clsx'
import useFormState, { actionTypes } from 'commons/useFormState'
import Formsy from 'formsy-react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { getPlanType } from 'commons/plan'
import { usePlan } from 'commons/useSubscription'
import React, { useCallback, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { usePlanBillingContext } from '../usePlanBillingContext'
import { useAppName } from 'commons/usePartner'
import { PlanBillingPaymentMethodCard } from './PlanBillingPaymentMethod'
import {
  RechargeSuccessDialog,
  PurchaseFailureDialog,
} from './PlanBillingChangePlanSummary'
import PlanBillingRechargePaymentMethodForm from './PlanBillingRechargePaymentMethodForm'
import PlanBillingPaymentMethodFormsy from './PlanBillingPaymentMethodFormsy'

const ResponseRechargeType = {
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
}

const useStyles = makeStyles((theme) => ({
  addButton: {
    color: theme.palette.secondary.main,
    'margin-right': 'auto',
  },
  onlyOnePaymentMethod: {
    'border-radius': '5px !important',
  },
  paymentMethod: {
    border: `1px solid lightgrey`,
    'padding-left': '16px',
    'border-bottom': 'none',
    '&:first-child': {
      'border-radius': '5px 5px  0 0',
    },
    '&:last-child': {
      'border-radius': '0 0 5px 5px',
      'border-bottom': '1px solid lightgrey',
    },
  },
}))

export function PlanBillingSetPaymentMethodForm({
  paymentMethods,
  refetchPaymentMethods,
  onClose,
}) {
  const { formatMessage } = useIntl()
  const [isShowAddPaymentMethodForm, setIsShowAddPaymentMethodForm] =
    useState(false)
  const [isShowDialogByResponseRecharge, setIsShowDialogByResponseRecharge] =
    useState(null)
  const classes = useStyles()
  const reduxDispatch = useDispatch()
  const dropinRef = useRef(null)
  const formRef = useRef(null)
  const currentPlan = usePlan()
  const [formState, dispatch] = useFormState()
  const [paymentMethodToDeleted, setPaymentMethodToDeleted] = useState(null)
  const isPlaceInRechargeablePeriod = useIsPlaceInRechargeablePeriod()
  const { refetch } = usePlanBillingContext()
  const isDisplayRadioRechargeForm =
    isPlaceInRechargeablePeriod &&
    paymentMethods.length > 1 &&
    !isShowAddPaymentMethodForm
  const [
    isShowConfirmDeletePaymentMethod,
    setIsShowConfirmDeletePaymentMethod,
  ] = useState(false)
  const [isPaymentMethodRequestable, setIsPaymentMethodRequestable] =
    useState(false)
  const appName = useAppName()

  const isDisabledSubmitButton = () => {
    return !isPaymentMethodRequestable
  }

  const isSelectedPaymentMethod = R.propEq('default', true)

  const handleInitialDropin = useCallback((dropin) => {
    dropinRef.current = dropin

    if (dropin.isPaymentMethodRequestable()) {
      // This will be true if you generated the client token
      // with a customer ID and there is a saved payment method
      // available to tokenize with that customer.
      setIsPaymentMethodRequestable(true)
    }

    dropin.on('paymentMethodRequestable', function (event) {
      setIsPaymentMethodRequestable(true)
    })

    dropin.on('noPaymentMethodRequestable', function () {
      setIsPaymentMethodRequestable(false)
    })
  }, [])

  const handleSubmit = async (value) => {
    if (R.has('isDefault', value)) {
      return handleCreatePaymentMethod(value)
    }
    return handleRechargePaymentMethod(value)
  }

  const handleRechargePaymentMethod = async (value) => {
    dispatch({ type: actionTypes.SUBMIT_INIT })
    try {
      await effects.apis.rechargePlan({
        token: R.propOr(
          R.o(R.prop('token'), R.find(isSelectedPaymentMethod))(paymentMethods),
          'rechargePaymentMethod',
          value,
        ),
      })
      setIsShowDialogByResponseRecharge(ResponseRechargeType.SUCCESS)
      refetch()
    } catch (error) {
      setIsShowDialogByResponseRecharge(ResponseRechargeType.FAILURE)
    } finally {
      setIsShowAddPaymentMethodForm(false)
      dispatch({ type: actionTypes.SUBMIT_SUCCESS })
    }
  }

  const handleCreatePaymentMethod = async (value) => {
    dispatch({ type: actionTypes.SUBMIT_INIT })
    try {
      const response = await dropinRef.current.requestPaymentMethod()
      await effects.apis.createPaymentMethod({
        default: R.prop('isDefault', value),
        paymentMethodNonce: R.prop('nonce', response),
      })
      refetchPaymentMethods()
    } catch (error) {
      reduxDispatch(MessageAlert.showNetworkError(formatMessage)(error))
    } finally {
      setIsShowAddPaymentMethodForm(false)
      dispatch({ type: actionTypes.SUBMIT_SUCCESS })
    }
  }

  const handleSetDefaultPaymentMethod = async (token) => {
    dispatch({ type: actionTypes.SUBMIT_INIT })
    try {
      await effects.apis.setDefaultPaymentMethod(token)
      refetchPaymentMethods()
    } catch (error) {
      reduxDispatch(MessageAlert.showNetworkError(formatMessage)(error))
    } finally {
      dispatch({ type: actionTypes.SUBMIT_SUCCESS })
    }
  }

  const handleDeletePaymentMethod = async (token) => {
    dispatch({ type: actionTypes.SUBMIT_INIT })
    try {
      await effects.apis.deletePaymentMethod({ token })
      refetchPaymentMethods()
    } catch (error) {
      reduxDispatch(MessageAlert.showNetworkError(formatMessage)(error))
    } finally {
      setIsShowConfirmDeletePaymentMethod(false)
      setPaymentMethodToDeleted(null)
      dispatch({ type: actionTypes.SUBMIT_SUCCESS })
    }
  }

  const handleInvalid = () => {
    dispatch({
      type: actionTypes.INVALID_FORM,
    })
  }

  const handleValid = () => {
    dispatch({
      type: actionTypes.VALID_FORM,
    })
  }

  return (
    <Formsy
      ref={formRef}
      className="flex flex-col overflow-hidden"
      onValid={handleValid}
      onInvalid={handleInvalid}
      onValidSubmit={handleSubmit}
    >
      <DialogContent className="p-24 py-0">
        <Typography>
          <FormattedMessage id="planAndBilling.paymentMethod.dialog.subTitle" />
        </Typography>
        {isDisplayRadioRechargeForm ? (
          <PlanBillingRechargePaymentMethodForm
            paymentMethods={paymentMethods}
            onClick={handleSetDefaultPaymentMethod}
            formState={formState}
            onDelete={(token) => {
              setPaymentMethodToDeleted(token)
              setIsShowConfirmDeletePaymentMethod(true)
            }}
          />
        ) : (
          <div className="mt-16">
            {paymentMethods.length ? (
              paymentMethods.map((paymentMethod) => {
                return (
                  <PlanBillingPaymentMethodCard
                    isDefault={paymentMethod.default}
                    onClick={handleSetDefaultPaymentMethod}
                    onDelete={(token) => {
                      setPaymentMethodToDeleted(token)
                      setIsShowConfirmDeletePaymentMethod(true)
                    }}
                    className={clsx(
                      'py-8 px-16',
                      classes.paymentMethod,
                      {
                        [classes.onlyOnePaymentMethod]:
                          paymentMethods.length === 1,
                      },
                      {
                        [classes.selectedPaymentMethod]:
                          isSelectedPaymentMethod(paymentMethod),
                        'pointer-events-none':
                          R.prop('isSubmitting', formState) ||
                          isSelectedPaymentMethod(paymentMethod),
                      },
                    )}
                    paymentMethod={paymentMethod}
                  />
                )
              })
            ) : (
              <div className="flex justify-center min-h-60">
                <CircularProgress size={50} />
              </div>
            )}
          </div>
        )}

        {isShowAddPaymentMethodForm && (
          <>
            <PlanBillingPaymentMethodFormsy
              className="my-16"
              name="paymentMethodNonce"
              isCustomerIncluded={false}
              onInitialDropin={handleInitialDropin}
            />
            <CheckboxFormsy
              className="flex-1"
              value={true}
              label={formatMessage({
                id: 'planAndBilling.paymentMethod.add.isDefault',
              })}
              name="isDefault"
              fullWidth
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isShowAddPaymentMethodForm ? (
          <div className="ltr:pr-16 rtl:pl-16">
            <CancelButton onClick={onClose} className="ltr:mr-16 rtl:ml-16" />
            <PrimaryButton
              isLoading={R.prop('isSubmitting', formState)}
              labelId="addBtn"
              disabled={isDisabledSubmitButton()}
              type="submit"
            />
          </div>
        ) : (
          <div className="flex justify-between w-full ltr:pr-16 rtl:pl-16">
            <PrimaryButton
              className={clsx(
                'ltr:pl-16 rtl:pr-16 normal-case',
                classes.addButton,
              )}
              variant="text"
              onClick={() => setIsShowAddPaymentMethodForm(true)}
            >
              <FormattedMessage id="planAndBilling.paymentMethod.addPaymentMethod" />
            </PrimaryButton>
            {isPlaceInRechargeablePeriod && (
              <PrimaryButton
                labelId="action.renew"
                disabled={R.prop('isSubmitting', formState)}
                isLoading={R.prop('isSubmitting', formState)}
                type="submit"
              />
            )}
          </div>
        )}
      </DialogActions>
      <ConfirmationDialog
        open={isShowConfirmDeletePaymentMethod}
        title={formatMessage({
          id: 'planAndBilling.paymentMethod.deleteConfirm.title',
        })}
        body={formatMessage(
          {
            id: 'planAndBilling.paymentMethod.deleteConfirm.body',
          },
          {
            appName,
          },
        )}
        CancelButton={
          <CancelButton
            onClick={() => setIsShowConfirmDeletePaymentMethod(false)}
          />
        }
        ConfirmButton={
          <PrimaryButton
            isLoading={R.prop('isSubmitting', formState)}
            onClick={() => {
              handleDeletePaymentMethod(paymentMethodToDeleted)
            }}
          >
            <FormattedMessage className="uppercase" id="action.remove" />
          </PrimaryButton>
        }
      />
      <RechargeSuccessDialog
        isOpen={isShowDialogByResponseRecharge === ResponseRechargeType.SUCCESS}
        onClose={() => {
          setIsShowDialogByResponseRecharge(null)
          onClose()
        }}
        getPlanProp={R.prop(R.__, currentPlan)}
        planType={getPlanType(R.prop('id', currentPlan))}
        nextPlan={currentPlan}
      />
      <PurchaseFailureDialog
        isOpen={isShowDialogByResponseRecharge === ResponseRechargeType.FAILURE}
        onClose={() => setIsShowDialogByResponseRecharge(null)}
      />
    </Formsy>
  )
}

PlanBillingSetPaymentMethodForm.propTypes = {
  paymentMethods: PropTypes.array,
}
